import { useEffect, useMemo, useState } from 'react';
import { PRODUCTS, productName } from '../../plugins/products';
import {
  useApi,
  configApiRef,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/frontend-plugin-api';
import type { ToolStatus } from './NavSection';

export type ToolGroup = 'workspace' | 'observability';

export type AiTool = {
  id: 'n8n' | 'evolution' | 'gateway' | 'grafana' | 'headlamp' | 'zabbix'
    | 'langfuse'
    | 'langflow';
  /** Which nav section the tool is listed under. */
  group: ToolGroup;
  title: string;
  /** Human-facing URL, resolved from `quodin.tools.<id>.url`. */
  url: string;
  /** Liveness path, relative to the Backstage proxy base URL. */
  probe: string;
  blurb: string;
};

/**
 * Static metadata; the URL is layered on from config at render time.
 *
 * Names and blurbs come from the product map rather than being written here, so
 * the UI presents capabilities instead of the projects implementing them, and
 * renaming one is a single edit. The probe paths keep the real service names —
 * they are routes, not copy.
 */
const TOOL_META: Omit<AiTool, 'url'>[] = [
  {
    id: 'n8n',
    group: 'workspace',
    title: productName('automation'),
    probe: '/n8n/healthz',
    blurb: PRODUCTS.automation.blurb,
  },
  {
    id: 'evolution',
    group: 'workspace',
    title: productName('messaging'),
    probe: '/evolution/',
    blurb: PRODUCTS.messaging.blurb,
  },
  {
    id: 'gateway',
    group: 'workspace',
    title: productName('gateway'),
    // The gateway's /healthz answers without a credential, so the '/gateway'
    // proxy entry carries none — the browser never borrows a gateway key.
    probe: '/gateway/healthz',
    blurb: PRODUCTS.gateway.blurb,
  },
  {
    id: 'grafana',
    group: 'observability',
    title: productName('dashboards'),
    probe: '/grafana/api/health',
    blurb: PRODUCTS.dashboards.blurb,
  },
  {
    id: 'headlamp',
    group: 'observability',
    title: productName('clusterConsole'),
    // oauth2-proxy's own liveness path: Headlamp sits behind the auth flow and
    // would answer a probe with a redirect.
    probe: '/headlamp/ping',
    blurb: PRODUCTS.clusterConsole.blurb,
  },
  {
    id: 'langfuse',
    group: 'workspace',
    title: productName('tracing'),
    // Its own public health endpoint answers without a session, unlike the UI
    // which sits behind the auth flow.
    probe: '/langfuse/api/public/health',
    blurb: PRODUCTS.tracing.blurb,
  },
  {
    id: 'langflow',
    group: 'workspace',
    title: productName('flowBuilder'),
    probe: '/langflow/health',
    blurb: PRODUCTS.flowBuilder.blurb,
  },
  {
    id: 'zabbix',
    group: 'observability',
    title: productName('infraMonitor'),
    // Behind the shared oauth2-proxy, so an unauthenticated probe gets a
    // redirect rather than the app; ping is the proxy's own liveness path.
    probe: '/zabbix/ping',
    blurb: PRODUCTS.infraMonitor.blurb,
  },
];

/**
 * The AI workspace as configured for this environment. Local development points
 * at the docker-compose ports; the cluster overrides these in
 * app-config.production.yaml.
 */
export const useAiTools = (): AiTool[] => {
  const config = useApi(configApiRef);
  return useMemo(
    () =>
      TOOL_META.map(meta => ({
        ...meta,
        url: config.getOptionalString(`quodin.tools.${meta.id}.url`) ?? '',
      })),
    [config],
  );
};

type HealthMap = Record<string, ToolStatus>;

const INITIAL: HealthMap = {
  n8n: 'unknown',
  evolution: 'unknown',
  gateway: 'unknown',
  grafana: 'unknown',
};

/**
 * Polls each tool through the backend proxy. A tool being unreachable is a
 * normal state (not every environment runs all three), so a failed probe maps
 * to 'down' rather than surfacing an error.
 */
export const useToolHealth = (intervalMs = 30_000): HealthMap => {
  const discoveryApi = useApi(discoveryApiRef);
  // fetchApi attaches the Backstage identity token; the proxy rejects
  // unauthenticated requests with a 401.
  const { fetch } = useApi(fetchApiRef);
  const [health, setHealth] = useState<HealthMap>(INITIAL);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      try {
        const base = await discoveryApi.getBaseUrl('proxy');
        const results = await Promise.all(
          TOOL_META.map(async tool => {
            try {
              const res = await fetch(`${base}${tool.probe}`);
              return [tool.id, res.ok ? 'up' : 'down'] as const;
            } catch {
              return [tool.id, 'down'] as const;
            }
          }),
        );
        if (!cancelled) {
          setHealth(Object.fromEntries(results) as HealthMap);
        }
      } catch {
        // Discovery itself failed — treat the whole workspace as unknown.
      }
    };

    check();
    const timer = setInterval(check, intervalMs);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [discoveryApi, fetch, intervalMs]);

  return health;
};
